import { useState } from "react"
import ModalPortal from "../../../components/Modal/ModalPortal"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import Button, { ButtonIcon } from "../../../components/Button/Button"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import { OperatingScheduleEntity } from "../../../Objects/OperatingSchedule/OperatingScheduleEntity"
import { useTranslation } from "react-i18next"
import { SIZE } from "../../../configuration/Sizes"
import THEME from "../../../configuration/Theme"
import { faClockFour, faClose } from "@fortawesome/free-solid-svg-icons"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import { companyMemory } from "../../../Objects/Memory/Memory"
import { DayOfWeekLabels } from "../../../Enum/EDayOfWeek"
import { useSaveCompany } from "../../../hooks/Company/useSaveCompany"
import { useGetCompany } from "../../../hooks/Company/useGetCompany"
import InputField from "../../../components/InputField/InputField"
import { Permissions } from "../../../Enum/PermissionsType"
import { CanView } from "../../../components/CanView/CanView"
import { usePermission } from "../../../hooks/Permissions/usePermission"
import { useRegister } from "../../../hooks/Register/useRegister"
import { ViewRegister } from "../../../components/ViewRegister/ViewRegister"
import GetSpace from "../../../utils/space"
import { SimpleToggle } from "../../../components/SimpleToggle/SimpleToggle"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"

interface Props {
  onClose: () => void
}

export const OperatingSchedulePopup: React.FC<Props> = ({ onClose }) => {
  const { t } = useTranslation("company")

  const { handleSaveOperatingSchedule } = useSaveCompany()

  const { handleGetCompany } = useGetCompany()

  const canEdite = !usePermission(Permissions.CanEditeCompany)

  const { handleEnableRegister, handleDisableRegister, isRegisterEnable } = useRegister()

  const cloneSchedules = () => companyMemory.operatingSchedules.map((schedule) => new OperatingScheduleEntity(schedule))

  const [data, setData] = useState<OperatingScheduleEntity[]>(() => cloneSchedules())

  const [allDaysOpenTime, setAllDaysOpenTime] = useState("")

  const [allDaysCloseTime, setAllDaysCloseTime] = useState("")

  const toggleDayEnable = (index: number, enable: boolean) => {
    const clone = data.map((item) => new OperatingScheduleEntity(item))

    clone[index].dayEnable = enable

    if (!enable) {
      clone[index].openTime = ""
      clone[index].closeTime = ""
    }

    setData(clone)
  }

  const handleTimeChange = (index: number, field: "openTime" | "closeTime", value: string) => {
    const clone = data.map((item) => new OperatingScheduleEntity(item))

    clone[index][field] = value

    setData(clone)
  }

  const handleApplySameTimeForAllDays = () => {
    if (!allDaysOpenTime || !allDaysCloseTime) return

    const clone = data.map((item) => new OperatingScheduleEntity(item))

    clone.forEach((schedule) => {
      schedule.dayEnable = true
      schedule.openTime = allDaysOpenTime
      schedule.closeTime = allDaysCloseTime
    })

    setData(clone)
  }

  const handleTrySave = () => {
    handleSaveOperatingSchedule(companyMemory.id, data, () => {
      handleGetCompany(() => {})
      onClose()
    })
  }

  const handleClose = () => {
    setData(cloneSchedules())

    onClose()
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert1}>
            <TitlePageResponsiveIconSmall
              title={t("label_title_company_Time")}
              icon={faClockFour}
              children={
                <div className="flex items-center gap-1">
                  <PopupHelpButton
                    helpKey="pages.myrestaurant.mycompany.operatingschedulepopup.1"
                    variant="global"
                    title={t("helpPopup:global.operatingSchedule.title")}
                    description={t("helpPopup:global.operatingSchedule.description")}
                    bullets={t("helpPopup:global.operatingSchedule.bullets", { returnObjects: true }) as string[]}
                  />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={handleClose}
                    size="small"
                    descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.operatingSchedulePopup.button.handleClose"
                  />
                </div>
              }
            />

            <div className="mt-2 space-y-2">
              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  e.stopPropagation()

                  handleApplySameTimeForAllDays()
                }}
                className="bg-orange-50 rounded-xl p-4 ring-2 ring-orange-100 border border-orange-200"
              >
                <h3 className="text-sm font-bold text-gray-800">{t("label_operating_schedule_apply_all_title")}</h3>

                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <InputField
                    type="time"
                    title={t("label_operating_schedule_open_time")}
                    placeholder={t("label_operating_schedule_open_time")}
                    onInvalidMessage={t("label_operating_schedule_open_time")}
                    value={allDaysOpenTime}
                    disable={canEdite}
                    required
                    onChange={(e) => setAllDaysOpenTime(e.target.value)}
                    descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.operatingSchedulePopup.input.operatingScheduleOpenTime"
                  />

                  <InputField
                    type="time"
                    title={t("label_operating_schedule_close_time")}
                    placeholder={t("label_operating_schedule_close_time")}
                    onInvalidMessage={t("label_operating_schedule_close_time")}
                    value={allDaysCloseTime}
                    disable={canEdite}
                    required
                    onChange={(e) => setAllDaysCloseTime(e.target.value)}
                    descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.operatingSchedulePopup.input.operatingScheduleCloseTime"
                  />
                </div>

                <div className="mt-4">
                  <CanView permission={Permissions.CanEditeCompany}>
                    <Button
                      label={t("label_operating_schedule_apply_all_button")}
                      typeButton="submit"
                      type="primary"
                      size="medium"
                      bgColor={THEME.colors.buttonBGSaveColor}
                      descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.operatingSchedulePopup.button.operatingScheduleApplyAll"
                    />
                  </CanView>
                </div>
              </form>

              {data.sort().map((item, index) => (
                <Animate key={item.day} type={AnimationTypes.fadeInTraslate} duration={`${0.6 * index}s`}>
                  <div className="bg-gray-50 shadow-sm rounded-xl p-4 ring-2 ring-gray-200">
                    <Animate key={item.day} type={AnimationTypes.fadeInTraslate}>
                      <div className="flex items-center justify-between gap-4 p-1 ">
                        <span className="font-bold text-sm text-black">
                          {t(`dateTimeSelector:days.${DayOfWeekLabels[item.day]}`)}
                        </span>

                        <SimpleToggle
                          title="Ativo"
                          value={item.dayEnable}
                          disabled={canEdite}
                          boxShadow="none"
                          animationDuration="1.2s"
                          onChange={(value) => toggleDayEnable(index, value)}
                          descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.operatingSchedulePopup.toggle.ativo"
                        />
                      </div>
                    </Animate>

                    {item.dayEnable && (
                      <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-3">
                        <InputField
                          type="time"
                          placeholder={""}
                          value={item.openTime}
                          disable={canEdite}
                          onChange={(e) => {
                            handleTimeChange(index, "openTime", e.target.value)
                          }}
                          descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.operatingSchedulePopup.input.input"
                        />

                        <span className="text-sm font-semibold text-gray-700 text-center">
                          {t("frontend:my_company.until")}
                        </span>

                        <InputField
                          type="time"
                          placeholder={""}
                          value={item.closeTime}
                          disable={canEdite}
                          onChange={(e) => {
                            handleTimeChange(index, "closeTime", e.target.value)
                          }}
                          descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.operatingSchedulePopup.input.input2"
                        />
                      </div>
                    )}
                  </div>
                </Animate>
              ))}
            </div>

            <div className={defaultStyle.divisionBar}></div>

            <section className={defaultStyle.buttonsController}>
              <CanView permission={Permissions.CanEditeCompany}>
                <Button
                  label={t("buttons:button_save")}
                  typeButton="button"
                  type="primary"
                  size="medium"
                  bgColor={THEME.colors.buttonBGSaveColor}
                  onClick={handleTrySave}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.operatingSchedulePopup.button.save"
                />
              </CanView>

              <GetSpace right={5} />

              <Button
                label={t("buttons:button_register")}
                typeButton="button"
                type="primary"
                size="medium"
                bgColor={THEME.colors.buttonBGRegisterColor}
                onClick={handleEnableRegister}
                descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.operatingSchedulePopup.button.register"
              />
            </section>

            {isRegisterEnable && (
              <ViewRegister id={companyMemory.id} typeEntity="CompanyEntityId" onClose={handleDisableRegister} />
            )}
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
